import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import {
  broilerDataByFarmUrl,
  updateActivitiesUrl,
  activitiesDataUrl,
  removeActivitiesUrl,
  hatcheryDataUrl,
} from "api/endpoints";
import useMediaQuery from "@mui/material/useMediaQuery";

const cm = (n) => `${n}cm`;

const ACTIVITY_DEFS = {
  vaccinations: {
    label: "Vaccinations",
    columns: [
      { key: "name", label: "Vaccine name", width: 3.0, input: { type: "text" } },
      { key: "dosage", label: "Dosage (ml)", width: 3.0, input: { type: "number" } },
      { key: "start", label: "Start date and time", width: 3.0, input: { type: "datetime-local" } },
      { key: "due", label: "Complete by", width: 3.0, input: { type: "datetime-local" } },
      { key: "completed", label: "Completed", width: 3.0, input: { type: "text" } },
    ],
  },
  water: {
    label: "Water Sanitation",
    columns: [
      { key: "product", label: "Sanitiser name", width: 3.0, input: { type: "text" } },
      { key: "dosage", label: "Dosage (ml)", width: 3.0, input: { type: "number" } },
      { key: "start", label: "Start date and time", width: 3.0, input: { type: "datetime-local" } },
      { key: "due", label: "Complete by", width: 3.0, input: { type: "datetime-local" } },
      { key: "completed", label: "Completed", width: 3.0, input: { type: "text" } },
    ],
  },
  litter: {
    label: "Litter Add/Remove",
    columns: [
      { key: "material", label: "Litter", width: 3.0, input: { type: "text" } },
      { key: "quantity", label: "Weight (kg)", width: 3.0, input: { type: "number" } },
      { key: "start", label: "Start date and time", width: 3.0, input: { type: "datetime-local" } },
      { key: "due", label: "Complete by", width: 3.0, input: { type: "datetime-local" } },
      { key: "completed", label: "Completed", width: 3.0, input: { type: "text" } },
    ],
  },
  facility: {
    label: "Facility Sanitation",
    columns: [
      { key: "task", label: "Sanitiser name", width: 3.0, input: { type: "text" } },
      { key: "dosage", label: "Dosage (ml)", width: 3.0, input: { type: "number" } },
      { key: "start", label: "Start date and time", width: 3.0, input: { type: "datetime-local" } },
      { key: "due", label: "Complete by", width: 3.0, input: { type: "datetime-local" } },
      { key: "completed", label: "Completed", width: 3.0, input: { type: "text" } },
    ],
  },
};

export default function ProductionActivities({ facilityId, cycleId, farmId }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const mobileTabsRef = React.useRef(null);
  const tabsPaperRef = React.useRef(null);
  const [isMobileTabs, setIsMobileTabs] = useState(false);
  const scrollAnimRef = React.useRef(null);
  const [activeKey, setActiveKey] = useState("vaccinations");
  const [addOpen, setAddOpen] = useState(false);
  const [rowsByKey, setRowsByKey] = useState({
    vaccinations: [],
    water: [],
    litter: [],
    facility: [],
  });
  const [form, setForm] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [primaryContact, setPrimaryContact] = useState("");
  const [secondaryContact, setSecondaryContact] = useState("");
  const [nameOptions, setNameOptions] = useState([]); // Vaccinations (type 15)
  const [waterOptions, setWaterOptions] = useState([]); // Sanitiser (type 14)
  const [litterOptions, setLitterOptions] = useState([]); // Litter (type 12)
  const [snack, setSnack] = useState({ open: false, message: "" });
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && `${v}`.length > 0);

  const def = ACTIVITY_DEFS[activeKey];
  const actionsWidth = 3.0; // cm, previous setting
  const totalWidth = useMemo(
    () => def.columns.reduce((s, c) => s + c.width, 0) + actionsWidth,
    [def]
  );

  const openAdd = () => {
    setForm({});
    setEditingIndex(null);
    setAddOpen(true);
  };

  const closeAdd = () => setAddOpen(false);

  const saveAdd = () => {
    // Convert various input date/time strings into a simple ISO-like string without timezone
    // Format: YYYY-MM-DD HH:mm (drop seconds/millis and the trailing 'T'/'Z')
    const toSimpleIsoMinutes = (v) => {
      if (!v) return null;
      const s = String(v).trim();
      // If already like 'YYYY-MM-DD HH:mm' just return
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return s;
      // If 'YYYY-MM-DDTHH:mm' -> replace 'T' with space
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s.replace("T", " ");
      // If seconds present, strip to minutes and replace 'T' with space
      const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
      if (m) return `${m[1]} ${m[2]}`;
      // Fallback: try Date and format to local YYYY-MM-DD HH:mm
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    };

    // Helpers for ISO with explicit offset (e.g., +02:00)
    const pad2 = (n) => String(n).padStart(2, "0");
    const pad3 = (n) => String(n).padStart(3, "0");
    const offsetToString = (offsetMin) => {
      const sign = offsetMin >= 0 ? "+" : "-";
      const abs = Math.abs(offsetMin);
      const hh = pad2(Math.floor(abs / 60));
      const mm = pad2(abs % 60);
      return `${sign}${hh}:${mm}`;
    };
    const dateToOffsetISOString = (date, offsetMin) => {
      const t = new Date(date.getTime() + offsetMin * 60000);
      const yyyy = t.getUTCFullYear();
      const mm = pad2(t.getUTCMonth() + 1);
      const dd = pad2(t.getUTCDate());
      const hh = pad2(t.getUTCHours());
      const mi = pad2(t.getUTCMinutes());
      const ss = pad2(t.getUTCSeconds());
      const ms = pad3(t.getUTCMilliseconds());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.${ms}${offsetToString(offsetMin)}`;
    };
    // Convert input into ISO with +02:00 (UTC+2) by default
    const toIsoOffset = (v, offsetMin = 120) => {
      if (!v) return null;
      if (v instanceof Date) return dateToOffsetISOString(v, offsetMin);
      const s = String(v).trim();
      // Already contains timezone -> treat as instant and convert to requested offset
      if (/Z$|[+-]\d{2}:\d{2}$/.test(s)) {
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : dateToOffsetISOString(d, offsetMin);
      }
      // Match YYYY-MM-DD[ T]HH:mm[:ss[.SSS]] without tz
      const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
      if (m) {
        const yyyyMmDd = m[1];
        const hh = pad2(Number(m[2]));
        const mi = pad2(Number(m[3]));
        const ss = pad2(m[4] ? Number(m[4]) : 0);
        const ms = pad3(m[5] ? Number(m[5]) : 0);
        return `${yyyyMmDd}T${hh}:${mi}:${ss}.${ms}${offsetToString(offsetMin)}`;
      }
      // Fallback: parse as date and convert
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : dateToOffsetISOString(d, offsetMin);
    };

    const commitLocal = (rowWithId) => {
      setRowsByKey((prev) => {
        const list = [...(prev[activeKey] || [])];
        if (editingIndex != null) {
          list[editingIndex] = rowWithId;
        } else {
          // Put newest on top so the 6x2 table (which shows first item)
          // displays the freshly-added record and its ID immediately
          list.unshift(rowWithId);
        }
        return { ...prev, [activeKey]: list };
      });
      setAddOpen(false);
      setEditingIndex(null);
      setSnack({ open: true, message: "Saved successfully" });
    };

    if (activeKey === "vaccinations") {
      // Robustly resolve nameId from the dropdown (type 15): prefer stored id; else map from label
      const resolvedNameId = (() => {
        if (form.nameId != null && `${form.nameId}`.length > 0) return Number(form.nameId);
        if (form.name && nameOptions && nameOptions.length) {
          const found = nameOptions.find((o) => String(o.label) === String(form.name));
          if (found && found.id != null) return Number(found.id);
        }
        return null;
      })();

      const isUpdate =
        editingIndex != null &&
        (form?.id != null || (rowsByKey[activeKey] || [])[editingIndex]?.id != null);
      const payload = {
        // Include id only when updating existing record
        ...(isUpdate ? { id: form?.id ?? (rowsByKey[activeKey] || [])[editingIndex]?.id } : {}),
        broilerId: facilityId,
        cycleId: cycleId,
        typeAct: 1,
        nameId: resolvedNameId,
        dosage: form.dosage != null && form.dosage !== "" ? Number(form.dosage) : null,
        // Enforce ISO timestamps for API with UTC+2
        startDate: toIsoOffset(form.start, 120),
        // Boolean required by port 89 API
        completed: !!form.completed,
        completedDateTime: toIsoOffset(form.due, 120),
        // On update include a bit/timestamp marker using current time (ISO, UTC+2)
        ...(isUpdate ? { completedBitTimestamp: toIsoOffset(new Date(), 120) } : {}),
      };
      // Debug trace to validate payload mapping during integration
      console.debug("Posting Vaccination activity", payload);
      const rowForUI = {
        id: form.id ?? "",
        name: form.name ?? "",
        nameId: resolvedNameId ?? form.nameId ?? null,
        dosage: form.dosage ?? "",
        start: toSimpleIsoMinutes(form.start) ?? "",
        due: toSimpleIsoMinutes(form.due) ?? "",
        completed: !!form.completed,
      };
      (async () => {
        try {
          const res = await axios.post(updateActivitiesUrl(), payload, {
            headers: { "Content-Type": "application/json" },
          });
          const returned = res?.data || {};
          // Robustly extract an ID from various possible response shapes
          const extractNewId = (d) => {
            if (d == null) return undefined;
            if (typeof d === "number") return d;
            if (typeof d === "string" && /^\d+$/.test(d)) return Number(d);
            if (d.id != null) return d.id;
            if (d.ID != null) return d.ID;
            if (d.insertId != null) return d.insertId;
            if (d.lastID != null) return d.lastID;
            if (d.lastId != null) return d.lastId;
            if (Array.isArray(d) && d.length) return extractNewId(d[0]);
            if (d.data != null) return extractNewId(d.data);
            if (d.result != null) return extractNewId(d.result);
            return undefined;
          };
          const newId = extractNewId(returned);
          // Update local list and also keep form in sync in case UI references it
          commitLocal({ ...rowForUI, id: isUpdate ? payload.id : newId ?? rowForUI.id });
          if (!isUpdate && newId != null) setForm((f) => ({ ...f, id: newId }));

          // Refresh from server to capture canonical data ordering/format
          try {
            await fetchActivities();
          } catch {}
        } catch (e) {
          // Fallback to local commit even if API fails, to not block UI during styling work
          commitLocal(rowForUI);
        }
      })();
      return;
    }
    if (activeKey === "water") {
      const resolvedNameId = (() => {
        if (form.nameId != null && `${form.nameId}`.length > 0) return Number(form.nameId);
        if (form.product && waterOptions && waterOptions.length) {
          const found = waterOptions.find((o) => String(o.label) === String(form.product));
          if (found && found.id != null) return Number(found.id);
        }
        return null;
      })();

      const isUpdate =
        editingIndex != null &&
        (form?.id != null || (rowsByKey[activeKey] || [])[editingIndex]?.id != null);
      const payload = {
        ...(isUpdate ? { id: form?.id ?? (rowsByKey[activeKey] || [])[editingIndex]?.id } : {}),
        broilerId: facilityId,
        cycleId: cycleId,
        typeAct: 2,
        nameId: resolvedNameId,
        dosage: form.dosage != null && form.dosage !== "" ? Number(form.dosage) : null,
        startDate: toIsoOffset(form.start, 120),
        completed: !!form.completed,
        completedDateTime: toIsoOffset(form.due, 120),
        ...(isUpdate ? { completedBitTimestamp: toIsoOffset(new Date(), 120) } : {}),
      };
      const rowForUI = {
        id: form.id ?? "",
        product: form.product ?? "",
        nameId: resolvedNameId ?? form.nameId ?? null,
        dosage: form.dosage ?? "",
        start: toSimpleIsoMinutes(form.start) ?? "",
        due: toSimpleIsoMinutes(form.due) ?? "",
        completed: !!form.completed,
      };
      (async () => {
        try {
          const res = await axios.post(updateActivitiesUrl(), payload, {
            headers: { "Content-Type": "application/json" },
          });
          const returned = res?.data || {};
          const extractNewId = (d) => {
            if (d == null) return undefined;
            if (typeof d === "number") return d;
            if (typeof d === "string" && /^\d+$/.test(d)) return Number(d);
            if (d.id != null) return d.id;
            if (d.ID != null) return d.ID;
            if (d.insertId != null) return d.insertId;
            if (d.lastID != null) return d.lastID;
            if (d.lastId != null) return d.lastId;
            if (Array.isArray(d) && d.length) return extractNewId(d[0]);
            if (d.data != null) return extractNewId(d.data);
            if (d.result != null) return extractNewId(d.result);
            return undefined;
          };
          const newId = extractNewId(returned);
          commitLocal({ ...rowForUI, id: isUpdate ? payload.id : newId ?? rowForUI.id });
          if (!isUpdate && newId != null) setForm((f) => ({ ...f, id: newId }));
          try {
            await fetchActivities();
          } catch {}
        } catch (e) {
          commitLocal(rowForUI);
        }
      })();
      return;
    }

    if (activeKey === "litter") {
      const resolvedNameId = (() => {
        if (form.nameId != null && `${form.nameId}`.length > 0) return Number(form.nameId);
        if (form.material && litterOptions && litterOptions.length) {
          const found = litterOptions.find((o) => String(o.label) === String(form.material));
          if (found && found.id != null) return Number(found.id);
        }
        return null;
      })();

      const isUpdate =
        editingIndex != null &&
        (form?.id != null || (rowsByKey[activeKey] || [])[editingIndex]?.id != null);
      const payload = {
        ...(isUpdate ? { id: form?.id ?? (rowsByKey[activeKey] || [])[editingIndex]?.id } : {}),
        broilerId: facilityId,
        cycleId: cycleId,
        typeAct: 3,
        nameId: resolvedNameId,
        dosage: form.quantity != null && form.quantity !== "" ? Number(form.quantity) : null,
        startDate: toIsoOffset(form.start, 120),
        completed: !!form.completed,
        completedDateTime: toIsoOffset(form.due, 120),
        ...(isUpdate ? { completedBitTimestamp: toIsoOffset(new Date(), 120) } : {}),
      };
      const rowForUI = {
        id: form.id ?? "",
        material: form.material ?? "",
        nameId: resolvedNameId ?? form.nameId ?? null,
        quantity: form.quantity ?? "",
        start: toSimpleIsoMinutes(form.start) ?? "",
        due: toSimpleIsoMinutes(form.due) ?? "",
        completed: !!form.completed,
      };
      (async () => {
        try {
          const res = await axios.post(updateActivitiesUrl(), payload, {
            headers: { "Content-Type": "application/json" },
          });
          const returned = res?.data || {};
          const extractNewId = (d) => {
            if (d == null) return undefined;
            if (typeof d === "number") return d;
            if (typeof d === "string" && /^\d+$/.test(d)) return Number(d);
            if (d.id != null) return d.id;
            if (d.ID != null) return d.ID;
            if (d.insertId != null) return d.insertId;
            if (d.lastID != null) return d.lastID;
            if (d.lastId != null) return d.lastId;
            if (Array.isArray(d) && d.length) return extractNewId(d[0]);
            if (d.data != null) return extractNewId(d.data);
            if (d.result != null) return extractNewId(d.result);
            return undefined;
          };
          const newId = extractNewId(returned);
          commitLocal({ ...rowForUI, id: isUpdate ? payload.id : newId ?? rowForUI.id });
          if (!isUpdate && newId != null) setForm((f) => ({ ...f, id: newId }));
          try {
            await fetchActivities();
          } catch {}
        } catch (e) {
          commitLocal(rowForUI);
        }
      })();
      return;
    }

    if (activeKey === "facility") {
      const resolvedNameId = (() => {
        if (form.nameId != null && `${form.nameId}`.length > 0) return Number(form.nameId);
        if (form.task && waterOptions && waterOptions.length) {
          const found = waterOptions.find((o) => String(o.label) === String(form.task));
          if (found && found.id != null) return Number(found.id);
        }
        return null;
      })();

      const isUpdate =
        editingIndex != null &&
        (form?.id != null || (rowsByKey[activeKey] || [])[editingIndex]?.id != null);
      const payload = {
        ...(isUpdate ? { id: form?.id ?? (rowsByKey[activeKey] || [])[editingIndex]?.id } : {}),
        broilerId: facilityId,
        cycleId: cycleId,
        typeAct: 4,
        nameId: resolvedNameId,
        dosage: form.dosage != null && form.dosage !== "" ? Number(form.dosage) : null,
        startDate: toIsoOffset(form.start, 120),
        completed: !!form.completed,
        completedDateTime: toIsoOffset(form.due, 120),
        ...(isUpdate ? { completedBitTimestamp: toIsoOffset(new Date(), 120) } : {}),
      };
      const rowForUI = {
        id: form.id ?? "",
        task: form.task ?? "",
        nameId: resolvedNameId ?? form.nameId ?? null,
        dosage: form.dosage ?? "",
        start: toSimpleIsoMinutes(form.start) ?? "",
        due: toSimpleIsoMinutes(form.due) ?? "",
        completed: !!form.completed,
      };
      (async () => {
        try {
          const res = await axios.post(updateActivitiesUrl(), payload, {
            headers: { "Content-Type": "application/json" },
          });
          const returned = res?.data || {};
          const extractNewId = (d) => {
            if (d == null) return undefined;
            if (typeof d === "number") return d;
            if (typeof d === "string" && /^\d+$/.test(d)) return Number(d);
            if (d.id != null) return d.id;
            if (d.ID != null) return d.ID;
            if (d.insertId != null) return d.insertId;
            if (d.lastID != null) return d.lastID;
            if (d.lastId != null) return d.lastId;
            if (Array.isArray(d) && d.length) return extractNewId(d[0]);
            if (d.data != null) return extractNewId(d.data);
            if (d.result != null) return extractNewId(d.result);
            return undefined;
          };
          const newId = extractNewId(returned);
          commitLocal({ ...rowForUI, id: isUpdate ? payload.id : newId ?? rowForUI.id });
          if (!isUpdate && newId != null) setForm((f) => ({ ...f, id: newId }));
          try {
            await fetchActivities();
          } catch {}
        } catch (e) {
          commitLocal(rowForUI);
        }
      })();
      return;
    }
  };

  // Load activities for current facility/cycle (all tabs)
  const fetchActivities = async () => {
    if (!facilityId || !cycleId) return;
    try {
      // Load broilerData mapping for type->options: 15 (vax), 14 (sanitiser), 12 (litter)
      let vacOpts = nameOptions;
      let sanOpts = waterOptions;
      let litOpts = litterOptions;
      if (!vacOpts.length || !sanOpts.length || !litOpts.length) {
        try {
          const res = await axios.get(broilerDataByFarmUrl(farmId));
          const arr = Array.isArray(res.data) ? res.data : [];
          const mapToOpts = (typeCode) =>
            arr
              .filter((x) => String(x?.type ?? x?.Type ?? "") === String(typeCode))
              .map((x) => ({
                id: x?.id ?? x?.ID,
                label:
                  x?.value ??
                  x?.Value ??
                  x?.description ??
                  x?.Description ??
                  x?.name ??
                  x?.Name ??
                  "",
              }))
              .filter((o) => o.id != null && o.label !== "");
          vacOpts = vacOpts.length ? vacOpts : mapToOpts(15);
          sanOpts = sanOpts.length ? sanOpts : mapToOpts(14);
          litOpts = litOpts.length ? litOpts : mapToOpts(12);
          setNameOptions(vacOpts);
          setWaterOptions(sanOpts);
          setLitterOptions(litOpts);
        } catch {
          // leave as is
        }
      }

      const res = await axios.get(activitiesDataUrl(facilityId, cycleId));
      const data = Array.isArray(res.data) ? res.data : [];

      // Helper: normalize date-time to 'YYYY-MM-DD HH:mm'
      const toSimpleIsoMinutes = (v) => {
        if (!v) return null;
        const s = String(v).trim();
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return s;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s.replace("T", " ");
        const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
        if (m) return `${m[1]} ${m[2]}`;
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
      };

      const labelFor = (opts, id) => {
        const opt = opts.find((o) => String(o.id) === String(id));
        return opt?.label ?? "";
      };

      const vaccinations = data
        .filter((r) => String(pick(r?.typeAct, r?.TypeAct, r?.type, r?.Type) ?? "") === "1")
        .map((r) => {
          const id = pick(r?.id, r?.ID);
          const nameId = pick(r?.nameId, r?.NameID, r?.nameid);
          return {
            id: id ?? "",
            nameId: nameId ?? null,
            name: labelFor(vacOpts, nameId) || pick(r?.name, r?.Name, r?.vaccine, r?.Vaccine) || "",
            dosage: pick(r?.dosage, r?.Dosage, r?.dose, r?.Dose) ?? "",
            start:
              toSimpleIsoMinutes(pick(r?.startDate, r?.StartDate, r?.start, r?.StartDateTime)) ??
              "",
            due:
              toSimpleIsoMinutes(
                pick(r?.completedDateTime, r?.CompletedDateTime, r?.due, r?.CompleteBy)
              ) ?? "",
            completed: Boolean(pick(r?.completed, r?.Completed, r?.isCompleted, r?.IsCompleted)),
          };
        })
        // Newest first by id when present
        .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

      const water = data
        .filter((r) => String(pick(r?.typeAct, r?.TypeAct, r?.type, r?.Type) ?? "") === "2")
        .map((r) => {
          const id = pick(r?.id, r?.ID);
          const nameId = pick(r?.nameId, r?.NameID, r?.nameid);
          return {
            id: id ?? "",
            nameId: nameId ?? null,
            product:
              labelFor(sanOpts, nameId) || pick(r?.product, r?.Product, r?.name, r?.Name) || "",
            dosage: pick(r?.dosage, r?.Dosage, r?.dose, r?.Dose) ?? "",
            start:
              toSimpleIsoMinutes(pick(r?.startDate, r?.StartDate, r?.start, r?.StartDateTime)) ??
              "",
            due:
              toSimpleIsoMinutes(
                pick(r?.completedDateTime, r?.CompletedDateTime, r?.due, r?.CompleteBy)
              ) ?? "",
            completed: Boolean(pick(r?.completed, r?.Completed, r?.isCompleted, r?.IsCompleted)),
          };
        })
        .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

      const litter = data
        .filter((r) => String(pick(r?.typeAct, r?.TypeAct, r?.type, r?.Type) ?? "") === "3")
        .map((r) => {
          const id = pick(r?.id, r?.ID);
          const nameId = pick(r?.nameId, r?.NameID, r?.nameid);
          return {
            id: id ?? "",
            nameId: nameId ?? null,
            material:
              labelFor(litOpts, nameId) || pick(r?.material, r?.Material, r?.name, r?.Name) || "",
            quantity: pick(r?.quantity, r?.Quantity, r?.qauntity, r?.dosage, r?.Dosage) ?? "",
            start:
              toSimpleIsoMinutes(pick(r?.startDate, r?.StartDate, r?.start, r?.StartDateTime)) ??
              "",
            due:
              toSimpleIsoMinutes(
                pick(r?.completedDateTime, r?.CompletedDateTime, r?.due, r?.CompleteBy)
              ) ?? "",
            completed: Boolean(pick(r?.completed, r?.Completed, r?.isCompleted, r?.IsCompleted)),
          };
        })
        .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

      const facility = data
        .filter((r) => String(pick(r?.typeAct, r?.TypeAct, r?.type, r?.Type) ?? "") === "4")
        .map((r) => {
          const id = pick(r?.id, r?.ID);
          const nameId = pick(r?.nameId, r?.NameID, r?.nameid);
          return {
            id: id ?? "",
            nameId: nameId ?? null,
            task: labelFor(sanOpts, nameId) || pick(r?.task, r?.Task, r?.name, r?.Name) || "",
            dosage: pick(r?.dosage, r?.Dosage, r?.dose, r?.Dose) ?? "",
            start:
              toSimpleIsoMinutes(pick(r?.startDate, r?.StartDate, r?.start, r?.StartDateTime)) ??
              "",
            due:
              toSimpleIsoMinutes(
                pick(r?.completedDateTime, r?.CompletedDateTime, r?.due, r?.CompleteBy)
              ) ?? "",
            completed: Boolean(pick(r?.completed, r?.Completed, r?.isCompleted, r?.IsCompleted)),
          };
        })
        .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

      setRowsByKey((prev) => ({ ...prev, vaccinations, water, litter, facility }));
    } catch (err) {
      console.error("Failed to load activities", err);
      // keep existing rows if any
    }
  };

  // Fetch when identifiers change
  React.useEffect(() => {
    fetchActivities();
  }, [facilityId, cycleId]);

  // Also load Primary/Secondary Contact from hatchery data for this facility/cycle
  React.useEffect(() => {
    const loadContacts = async () => {
      if (!facilityId || !cycleId) return;
      try {
        const res = await axios.get(hatcheryDataUrl(facilityId));
        const data = Array.isArray(res.data) ? res.data : [];
        // Normalize minimal fields and filter by cycle
        const rows = data.map((row) => ({
          cycleId: pick(row?.cycleId, row?.CycleID, row?.cycleID, row?.id, row?.ID),
          primary: pick(row?.primaryNumber, row?.PrimaryNumber, row?.primary_number),
          secondary: pick(row?.secondaryNumber, row?.SecondaryNumber, row?.secondary_number),
        }));
        const match = rows.find((r) => r.cycleId != null && String(r.cycleId) === String(cycleId));
        if (match) {
          setPrimaryContact(match.primary ?? "");
          setSecondaryContact(match.secondary ?? "");
        }
      } catch (e) {
        // Silent: do not block UI if hatchery contacts not available
      }
    };
    loadContacts();
  }, [facilityId, cycleId]);

  // Center the selected mobile tab within the scroller (vertical carousel) with eased animation
  const centerSelectedMobileTab = React.useCallback(() => {
    if (!isMobileTabs) return;
    const root = mobileTabsRef.current;
    if (!root) return;
    const scroller = root.querySelector?.(".MuiTabs-scroller");
    const selected = root.querySelector?.('[role="tab"][aria-selected="true"]');
    if (!scroller || !selected) return;
    const offsetTop = selected.offsetTop || 0;
    const targetTop = offsetTop - (scroller.clientHeight - selected.clientHeight) / 2;
    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const to = Math.max(0, Math.min(targetTop, maxScroll));

    // Cancel any running animation
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);

    const from = scroller.scrollTop;
    const change = to - from;
    if (Math.abs(change) < 1) return; // already centered

    const duration = 1000; // ms: slower easing
    const start = performance.now();
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(progress);
      scroller.scrollTop = from + change * eased;
      if (progress < 1) {
        scrollAnimRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimRef.current = null;
      }
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  }, [isMobileTabs]);

  React.useEffect(() => {
    const id = setTimeout(centerSelectedMobileTab, 120);
    return () => clearTimeout(id);
  }, [activeKey, isMobileTabs, addOpen, centerSelectedMobileTab]);

  React.useEffect(() => {
    const onResize = () => centerSelectedMobileTab();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [centerSelectedMobileTab]);

  // Allow manual scrolling alongside click-to-click animation
  React.useEffect(() => {
    // no-op
  }, [isMobileTabs]);

  // Determine mobile tabs mode based on viewport or measured panel width
  React.useEffect(() => {
    const compute = () => {
      const w = tabsPaperRef.current?.clientWidth || 0;
      setIsMobileTabs(isSmallScreen || (w > 0 && w <= 420));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [isSmallScreen, addOpen]);

  const openEdit = (idx) => {
    const list = rowsByKey[activeKey] || [];
    setForm(list[idx] || {});
    setEditingIndex(idx);
    setAddOpen(true);
  };

  const handleDelete = async (idx) => {
    const list = rowsByKey[activeKey] || [];
    const target = list[idx];
    const id = target?.id;
    if (!target) return;

    const confirmed =
      typeof window !== "undefined"
        ? window.confirm("Are you sure you want to delete this record?")
        : true;
    if (!confirmed) return;

    // If there's no id, just remove locally
    if (id == null || id === "") {
      setRowsByKey((prev) => {
        const copy = [...(prev[activeKey] || [])];
        copy.splice(idx, 1);
        return { ...prev, [activeKey]: copy };
      });
      setSnack({ open: true, message: "Record removed" });
      return;
    }

    try {
      await axios.post(
        removeActivitiesUrl(),
        { id },
        { headers: { "Content-Type": "application/json" } }
      );
      // Refresh from server for canonical state
      await fetchActivities();
      setSnack({ open: true, message: "Record deleted" });
    } catch (err) {
      console.error("Failed to delete activity", err);
      // Fallback: optimistic local removal
      setRowsByKey((prev) => {
        const copy = [...(prev[activeKey] || [])];
        copy.splice(idx, 1);
        return { ...prev, [activeKey]: copy };
      });
      setSnack({ open: true, message: "Deleted locally (server failed)" });
    }
  };

  // Load dropdown options for active tab when the dialog opens
  React.useEffect(() => {
    const load = async () => {
      if (!addOpen) return;
      try {
        const res = await axios.get(broilerDataByFarmUrl(farmId));
        const arr = Array.isArray(res.data) ? res.data : [];
        const mapToOpts = (typeCode) =>
          arr
            .filter((x) => String(x?.type ?? x?.Type ?? "") === String(typeCode))
            .map((x) => ({
              id: x?.id ?? x?.ID,
              label:
                x?.value ??
                x?.Value ??
                x?.description ??
                x?.Description ??
                x?.name ??
                x?.Name ??
                "",
            }))
            .filter((o) => o.id != null && o.label !== "");
        const vac = mapToOpts(15);
        const san = mapToOpts(14);
        const lit = mapToOpts(12);
        setNameOptions(vac);
        setWaterOptions(san);
        setLitterOptions(lit);
        // Infer nameId from current form label field
        if (
          activeKey === "vaccinations" &&
          (form?.nameId == null || `${form.nameId}`.length === 0) &&
          form?.name
        ) {
          const found = vac.find((o) => String(o.label) === String(form.name));
          if (found) setForm((f) => ({ ...f, nameId: found.id }));
        }
        if (
          activeKey === "water" &&
          (form?.nameId == null || `${form.nameId}`.length === 0) &&
          form?.product
        ) {
          const found = san.find((o) => String(o.label) === String(form.product));
          if (found) setForm((f) => ({ ...f, nameId: found.id }));
        }
        if (
          activeKey === "litter" &&
          (form?.nameId == null || `${form.nameId}`.length === 0) &&
          form?.material
        ) {
          const found = lit.find((o) => String(o.label) === String(form.material));
          if (found) setForm((f) => ({ ...f, nameId: found.id }));
        }
        if (
          activeKey === "facility" &&
          (form?.nameId == null || `${form.nameId}`.length === 0) &&
          form?.task
        ) {
          const found = san.find((o) => String(o.label) === String(form.task));
          if (found) setForm((f) => ({ ...f, nameId: found.id }));
        }
      } catch (e) {
        setNameOptions([]);
        setWaterOptions([]);
        setLitterOptions([]);
      }
    };
    load();
  }, [addOpen, activeKey]);

  return (
    <Box>
      {/* Contacts row */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: "0.2cm", mb: 1 }}>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Primary Contact"
            value={primaryContact}
            onChange={(e) => setPrimaryContact(e.target.value)}
            size="medium"
            sx={{ width: { xs: "100%", sm: "60%" } }}
            InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Secondary Contact"
            value={secondaryContact}
            onChange={(e) => setSecondaryContact(e.target.value)}
            size="medium"
            sx={{ width: { xs: "100%", sm: "60%" } }}
            InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
          />
        </Grid>
      </Grid>

      {/* Selection bar + table (offset adjusted) */}
      <Box sx={{ mt: "1cm" }}>
        {/* Segmented tabs */}
        <Paper
          ref={tabsPaperRef}
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 1,
            overflow: "hidden",
            border: "none",
            boxShadow: "none",
            bgcolor: "transparent",
            width: "100%",
            mx: 0,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 0.5,
                bgcolor: "grey.200",
                borderRadius: 1,
                width: { xs: "100%", md: "70%" },
              }}
            >
              {[
                { key: "vaccinations", label: ACTIVITY_DEFS.vaccinations.label },
                { key: "water", label: ACTIVITY_DEFS.water.label },
                { key: "litter", label: ACTIVITY_DEFS.litter.label },
                { key: "facility", label: ACTIVITY_DEFS.facility.label },
              ].map((t) => (
                <Button
                  key={t.key}
                  onClick={() => setActiveKey(t.key)}
                  variant={activeKey === t.key ? "outlined" : "text"}
                  sx={{
                    color: "text.primary",
                    bgcolor: activeKey === t.key ? "white" : "grey.200",
                    borderColor: activeKey === t.key ? "info.main" : "transparent",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Table */}
        <Paper
          variant="outlined"
          sx={{
            p: 0,
            pt: "0.5cm",
            pb: "0.5cm",
            borderRadius: 0,
            mt: "0.4cm",
            position: "relative",
          }}
        >
          {/* Add row button anchored to the outer component */}
          <Box sx={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}>
            <IconButton
              size="small"
              onClick={openAdd}
              aria-label="add"
              sx={{
                width: 28,
                height: 28,
                bgcolor: "grey.100",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
                "&:hover": { bgcolor: "grey.200" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ mt: "0.5cm", ml: "0.5cm", width: "calc(100% - 0.5cm)" }}>
            {/* Dynamic activities table (hidden) */}
            {false && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TableContainer component={Box} sx={{ boxShadow: "none" }}>
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: cm(totalWidth),
                      minWidth: cm(totalWidth),
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      {def.columns.map((c) => (
                        <col key={c.key} style={{ width: cm(c.width) }} />
                      ))}
                      <col style={{ width: cm(actionsWidth) }} />
                    </colgroup>
                    <TableHead>
                      <TableRow sx={{ height: "0.768cm", bgcolor: "grey.100" }}>
                        {def.columns.map((c) => (
                          <TableCell key={c.key} sx={{ py: 0.75, fontWeight: 700 }}>
                            {c.label}
                          </TableCell>
                        ))}
                        {/* Keep an empty header cell to preserve layout width */}
                        <TableCell sx={{ p: 0.5 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(rowsByKey[activeKey] || []).length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={def.columns.length + 1}
                            align="center"
                            sx={{ py: 6, color: "text.secondary" }}
                          >
                            No data
                          </TableCell>
                        </TableRow>
                      ) : (
                        (rowsByKey[activeKey] || []).map((row, idx) => (
                          <TableRow key={idx} sx={{ height: "0.512cm" }}>
                            {def.columns.map((c) => (
                              <TableCell key={c.key}>{row[c.key] ?? ""}</TableCell>
                            ))}
                            <TableCell>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  gap: 1,
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                }}
                              >
                                <EditIcon
                                  fontSize="inherit"
                                  onClick={() => openEdit(idx)}
                                  sx={{ fontSize: 36, color: "text.secondary", cursor: "pointer" }}
                                />
                                <DeleteIcon
                                  fontSize="inherit"
                                  onClick={() => handleDelete(idx)}
                                  sx={{ fontSize: 36, color: "text.secondary", cursor: "pointer" }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {/* Simple 6x2 table populated with current activity */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "0.5cm",
                position: "relative",
              }}
            >
              <TableContainer component={Box} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "23.06cm",
                    minWidth: "23.06cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontFamily: "inherit",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                      p: 0.5,
                      textAlign: "center",
                      color: "text.primary",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "1.75cm" }} />
                    <col style={{ width: "4.62cm" }} />
                    <col style={{ width: "3.85cm" }} />
                    <col style={{ width: "4.62cm" }} />
                    <col style={{ width: "3.85cm" }} />
                    {/* Completed column reduced by 15% (from 3.08cm) */}
                    <col style={{ width: "2.62cm" }} />
                    {/* New actions column (same width as ID) */}
                    <col style={{ width: "1.75cm" }} />
                  </colgroup>
                  <TableBody>
                    {/* Header row with ID in first column */}
                    <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      {def.columns.slice(0, 5).map((c) => (
                        <TableCell key={c.key} sx={{ fontWeight: 700 }}>
                          {c.label}
                        </TableCell>
                      ))}
                      {/* Actions header (blank) */}
                      <TableCell sx={{ fontWeight: 700 }} />
                    </TableRow>
                    {/* Data rows for all activities in the current tab */}
                    {(rowsByKey[activeKey] || []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      (rowsByKey[activeKey] || []).map((r, idx) => (
                        <TableRow key={r.id ?? idx} sx={{ height: "0.64cm" }}>
                          <TableCell>{r.id ?? ""}</TableCell>
                          {def.columns.slice(0, 5).map((c) => (
                            <TableCell key={c.key}>
                              {c.key === "completed" ? (
                                <Checkbox
                                  size="small"
                                  checked={Boolean(
                                    r.completed === true ||
                                      r.completed === "True" ||
                                      r.completed === 1 ||
                                      r.completed === "1"
                                  )}
                                  disabled
                                />
                              ) : (
                                r[c.key] ?? ""
                              )}
                            </TableCell>
                          ))}
                          {/* Actions cell */}
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                fontSize="inherit"
                                onClick={() => openEdit(idx)}
                                sx={{ fontSize: 45, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                fontSize="inherit"
                                onClick={() => handleDelete(idx)}
                                sx={{ fontSize: 45, color: "text.secondary", cursor: "pointer" }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Action icons moved into the Completed column cell for stable positioning */}
            </Box>
            {/* Success Snackbar */}
            <Snackbar
              open={snack.open}
              autoHideDuration={2500}
              onClose={() => setSnack({ open: false, message: "" })}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={
                isSmallScreen
                  ? {}
                  : {
                      left: "50% !important",
                      top: "50% !important",
                      bottom: "auto !important",
                      transform: "translate(-50%, -50%)",
                    }
              }
            >
              <Alert
                onClose={() => setSnack({ open: false, message: "" })}
                severity="success"
                sx={{ width: "100%", fontSize: "0.95rem" }}
              >
                {snack.message}
              </Alert>
            </Snackbar>
          </Box>
        </Paper>
      </Box>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={closeAdd} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2, position: "relative" }}>
          {ACTIVITY_DEFS[activeKey].label}
          <IconButton
            aria-label="close"
            onClick={closeAdd}
            size="small"
            color="inherit"
            title="Close"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 2,
              color: (theme) => theme.palette.grey[700],
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            {def.columns.map((c) => {
              // Special handling for dropdowns by tab and the completed checkbox
              if (activeKey === "vaccinations" && c.key === "name") {
                return (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <TextField
                      select
                      label={c.label}
                      value={form.nameId ?? ""}
                      onChange={(e) => {
                        const opt = nameOptions.find(
                          (o) => String(o.id) === String(e.target.value)
                        );
                        setForm((f) => ({ ...f, nameId: e.target.value, name: opt?.label ?? "" }));
                      }}
                      fullWidth
                      size="medium"
                      sx={{
                        "& .MuiInputBase-root": { height: "calc(52px - 2mm)" },
                        "& .MuiInputBase-input": { fontSize: "0.95rem" },
                      }}
                      SelectProps={{
                        renderValue: (v) => {
                          const opt = nameOptions.find((o) => String(o.id) === String(v));
                          return opt?.label ?? "";
                        },
                      }}
                      InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
                    >
                      {nameOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          No options
                        </MenuItem>
                      ) : (
                        nameOptions.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.label}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                );
              }

              if (activeKey === "water" && c.key === "product") {
                return (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <TextField
                      select
                      label={c.label}
                      value={form.nameId ?? ""}
                      onChange={(e) => {
                        const opt = waterOptions.find(
                          (o) => String(o.id) === String(e.target.value)
                        );
                        setForm((f) => ({
                          ...f,
                          nameId: e.target.value,
                          product: opt?.label ?? "",
                        }));
                      }}
                      fullWidth
                      size="medium"
                      sx={{
                        "& .MuiInputBase-root": { height: "calc(52px - 2mm)" },
                        "& .MuiInputBase-input": { fontSize: "0.95rem" },
                      }}
                      SelectProps={{
                        renderValue: (v) => {
                          const opt = waterOptions.find((o) => String(o.id) === String(v));
                          return opt?.label ?? "";
                        },
                      }}
                      InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
                    >
                      {waterOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          No options
                        </MenuItem>
                      ) : (
                        waterOptions.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.label}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                );
              }

              if (activeKey === "litter" && c.key === "material") {
                return (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <TextField
                      select
                      label={c.label}
                      value={form.nameId ?? ""}
                      onChange={(e) => {
                        const opt = litterOptions.find(
                          (o) => String(o.id) === String(e.target.value)
                        );
                        setForm((f) => ({
                          ...f,
                          nameId: e.target.value,
                          material: opt?.label ?? "",
                        }));
                      }}
                      fullWidth
                      size="medium"
                      sx={{
                        "& .MuiInputBase-root": { height: "calc(52px - 2mm)" },
                        "& .MuiInputBase-input": { fontSize: "0.95rem" },
                      }}
                      SelectProps={{
                        renderValue: (v) => {
                          const opt = litterOptions.find((o) => String(o.id) === String(v));
                          return opt?.label ?? "";
                        },
                      }}
                      InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
                    >
                      {litterOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          No options
                        </MenuItem>
                      ) : (
                        litterOptions.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.label}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                );
              }

              if (activeKey === "facility" && c.key === "task") {
                return (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <TextField
                      select
                      label={c.label}
                      value={form.nameId ?? ""}
                      onChange={(e) => {
                        const opt = waterOptions.find(
                          (o) => String(o.id) === String(e.target.value)
                        );
                        setForm((f) => ({ ...f, nameId: e.target.value, task: opt?.label ?? "" }));
                      }}
                      fullWidth
                      size="medium"
                      sx={{
                        "& .MuiInputBase-root": { height: "calc(52px - 2mm)" },
                        "& .MuiInputBase-input": { fontSize: "0.95rem" },
                      }}
                      SelectProps={{
                        renderValue: (v) => {
                          const opt = waterOptions.find((o) => String(o.id) === String(v));
                          return opt?.label ?? "";
                        },
                      }}
                      InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
                    >
                      {waterOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          No options
                        </MenuItem>
                      ) : (
                        waterOptions.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.label}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                );
              }

              if (c.key === "completed") {
                return (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
                      <Checkbox
                        checked={!!form.completed}
                        onChange={(e) => setForm((f) => ({ ...f, completed: e.target.checked }))}
                        sx={{ mr: 1 }}
                      />
                      <Box component="span">Completed</Box>
                    </Box>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} key={c.key}>
                  <TextField
                    label={c.label}
                    type={c.input?.type || "text"}
                    value={form[c.key] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [c.key]: e.target.value }))}
                    fullWidth
                    size="medium"
                    InputLabelProps={{
                      sx: { fontSize: "0.95rem" },
                      ...(c.input?.type?.includes("time") ? { shrink: true } : {}),
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button variant="contained" onClick={saveAdd} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ProductionActivities.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  farmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
